import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Search, ChevronRight } from 'lucide-react';
import { designTokens, getScoreColor } from '../styles/design-tokens';
import { useTheme } from '../context/ThemeContext';
import { FilterChip } from './ui/FilterChip';
import { SavedLocationsSection } from './SavedLocationsSection';
import { fetchZoneList, type Filters, type PipelineFilters, type ZoneListEntry } from '../lib/api';
import type { OperatorInfo } from './MapView';
import {
  focusSummary, groupByCompany, operatorsForCompany, searchCompanies, unionBbox,
  type Bbox, type OperatorCatalogEntry,
} from '../lib/operator-focus';
import { ONE_OFF_SITES, isOneOffZone } from '../lib/one-off-sites';
import { MemphisResearchSection } from './memphis/MemphisResearchSection';
import type { MemphisLayers } from '../data/memphis-research';
import { EtResearchSection } from './et/EtResearchSection';
import type { EtLayers, EtResearchPayload } from '../data/et-research';
import { COUNTIES } from '../data/counties';

interface FilterSidebarProps {
  filters: Filters;
  onChange: (filters: Filters) => void;
  pipelineFilters: PipelineFilters;
  onPipelineFiltersChange: (filters: PipelineFilters) => void;
  showFederalLands: boolean;
  onToggleFederalLands: () => void;
  showCrownPineTracts: boolean;
  onToggleCrownPineTracts: () => void;
  showAssemblyGroups: boolean;
  onToggleAssemblyGroups: () => void;
  showGathering: boolean;
  onToggleGathering: () => void;
  showGasTransmission: boolean;
  onToggleGasTransmission: () => void;
  showLiquidsTransmission: boolean;
  onToggleLiquidsTransmission: () => void;
  anyPipelineActive: boolean;
  onToggleAllPipelines: () => void;
  showDesertSw: boolean;
  onToggleDesertSw: () => void;
  operators: OperatorInfo[];
  hiddenOperators: Set<string>;
  onToggleOperator: (name: string) => void;
  onSetOperatorsHidden: (names: string[], hidden: boolean) => void;
  onShowAllOperators: () => void;
  operatorCatalog: OperatorCatalogEntry[];
  onFitBounds: (bbox: Bbox) => void;
  showPowerInfra: boolean;
  onTogglePowerInfra: () => void;
  powerLoading: boolean;
  showOsmSwitchyards: boolean;
  onToggleOsmSwitchyards: () => void;
  osmSwitchyardsHifldNewOnly: boolean;
  onToggleOsmSwitchyardsHifldNewOnly: () => void;
  osmSwitchyardsLoading: boolean;
  showGridCapacity: boolean;
  onToggleGridCapacity: () => void;
  gridCapacityLoading: boolean;
  gridCapacityTiers: Set<string>;
  onToggleGridCapacityTier: (tier: string) => void;
  showQueueHeatmap: boolean;
  onToggleQueueHeatmap: () => void;
  queueHeatmapLoading: boolean;
  showCongestionHeatmap: boolean;
  onToggleCongestionHeatmap: () => void;
  congestionHeatmapLoading: boolean;
  showElectricTerritories: boolean;
  onToggleElectricTerritories: () => void;
  showAirportZones: boolean;
  onToggleAirportZones: () => void;
  airportLoading: boolean;
  airportFilter: 'all' | 'commercial';
  onAirportFilterChange: (filter: 'all' | 'commercial') => void;
  showParcels: boolean;
  onToggleParcels: () => void;
  parcelLoading?: boolean;
  showFiber: boolean;
  onToggleFiber: () => void;
  fiberLoading: boolean;
  showSegra: boolean;
  onToggleSegra: () => void;
  showZayo: boolean;
  onToggleZayo: () => void;
  showFiberLight: boolean;
  onToggleFiberLight: () => void;
  showNtiaMiddleMile: boolean;
  onToggleNtiaMiddleMile: () => void;
  showFiberRowPipelines: boolean;
  onToggleFiberRowPipelines: () => void;
  showIxpFacilities: boolean;
  onToggleIxpFacilities: () => void;
  showWater: boolean;
  onToggleWater: () => void;
  waterLoading: boolean;
  showDataCenters: boolean;
  onToggleDataCenters: () => void;
  dcLoading: boolean;
  showFloodZones: boolean;
  onToggleFloodZones: () => void;
  floodZoneLoading?: boolean;
  showTwdbFloodHazards: boolean;
  onToggleTwdbFloodHazards: () => void;
  twdbFloodHazardsLoading?: boolean;
  showHighways: boolean;
  onToggleHighways: () => void;
  highwayLoading?: boolean;
  showRoads: boolean;
  onToggleRoads: () => void;
  roadsLoading?: boolean;
  showOsmPipelineCandidates?: boolean;
  onToggleOsmPipelineCandidates?: () => void;
  osmPipelineCandidatesLoading?: boolean;
  memphisLayers?: MemphisLayers;
  etLayers?: EtLayers;
  etData?: EtResearchPayload | null;
  etLoading?: boolean;
  etError?: string | null;
  onEtLoad?: () => void;
  onEtLayersChange?: (layers: EtLayers) => void;
  onEtZoom?: (bounds: Bbox) => void;
  onToggleAllEt?: () => void;
  onMemphisLayersChange?: (layers: MemphisLayers) => void;
  onMemphisZoom?: (bounds: Bbox) => void;
  onToggleAllMemphis?: () => void;
  showGeorgiaSites?: boolean;
  onToggleGeorgiaSites?: () => void;
  showGeorgiaEvidence?: boolean;
  onToggleGeorgiaEvidence?: () => void;
  anyGeorgiaActive?: boolean;
  onToggleAllGeorgia?: () => void;
  onZoomToGeorgiaSite?: () => void;
  showRailroads: boolean;
  onToggleRailroads: () => void;
  railroadLoading?: boolean;
  showGasWells: boolean;
  onToggleGasWells: () => void;
  showOilWells: boolean;
  onToggleOilWells: () => void;
  wellsLoading: boolean;
  showStateTrustLands: boolean;
  onToggleStateTrustLands: () => void;
  trustLandLoading: boolean;
  showProtectedAreas: boolean;
  onToggleProtectedAreas: () => void;
  showOperatorAois: boolean;
  onToggleOperatorAois: () => void;
  showLandListings: boolean;
  onToggleLandListings: () => void;
  listingsLoading: boolean;
  showGridListings: boolean;
  onToggleGridListings: () => void;
  gridListingsLoading: boolean;
  showGasPlants: boolean;
  onToggleGasPlants: () => void;
  gasPlantLoading: boolean;
  showCompressors: boolean;
  onToggleCompressors: () => void;
  showReceiptDelivery: boolean;
  onToggleReceiptDelivery: () => void;
  showInterconnects: boolean;
  onToggleInterconnects: () => void;
  showGasStorage: boolean;
  onToggleGasStorage: () => void;
  gasInfraLoading: boolean;
  anyGasSupplyActive: boolean;
  onToggleAllGasSupply: () => void;
  showEpaZones: boolean;
  onToggleEpaZones: () => void;
  epaZoneLoading: boolean;
  showEjScreen: boolean;
  onToggleEjScreen: () => void;
  ejScreenLoading: boolean;
  showCriticalHabitat: boolean;
  onToggleCriticalHabitat: () => void;
  showWetlands: boolean;
  onToggleWetlands: () => void;
  showSeismicRisk: boolean;
  onToggleSeismicRisk: () => void;
  showUrbanAreas: boolean;
  onToggleUrbanAreas: () => void;
  showWildfire: boolean;
  onToggleWildfire: () => void;
  wildfireLoading: boolean;
  showMineSubsidence: boolean;
  onToggleMineSubsidence: () => void;
  showAmlSites: boolean;
  onToggleAmlSites: () => void;
  anyFiberActive: boolean;
  onToggleAllFiber: () => void;
  anyInfraActive: boolean;
  onToggleAllInfra: () => void;
  anyWaterActive: boolean;
  onToggleAllWater: () => void;
  showTwdbWells: boolean;
  onToggleTwdbWells: () => void;
  showBrackish: boolean;
  onToggleBrackish: () => void;
  showTdsSamples: boolean;
  onToggleTdsSamples: () => void;
  showWaterRegulatoryZones: boolean;
  onToggleWaterRegulatoryZones: () => void;
  anyEnvActive: boolean;
  onToggleAllEnv: () => void;
  anyLandActive: boolean;
  onToggleAllLand: () => void;
  showSlopeDrape: boolean;
  onToggleSlopeDrape: () => void;
  showSlope1m: boolean;
  onToggleSlope1m: () => void;
  slopeDrapeOpacity: number;
  onSlopeDrapeOpacityChange: (v: number) => void;
  activeLayerCount: number;
  onResetAllLayers: () => void;
  showSavedLocations: boolean;
  onToggleSavedLocations: () => void;
  savedLocations: import('../lib/api').SavedLocation[];
  savedLocationsLoading: boolean;
  onClickSavedLocation: (loc: import('../lib/api').SavedLocation) => void;
  onRenameSavedLocation: (id: number, name: string) => Promise<import('../lib/api').SavedLocation>;
  onDeleteSavedLocation: (id: number) => Promise<void>;
}

const ToggleRow: React.FC<{
  label: string; active: boolean; onToggle: () => void;
  color: string; loading?: boolean; count?: number;
}> = ({ label, active, onToggle, color, loading, count }) => {
  const { theme } = useTheme();
  return (
    <button
      onClick={onToggle}
      style={{
        width: '100%', padding: '5px 8px', fontSize: '12px',
        background: 'transparent', border: 'none', cursor: 'pointer',
        textAlign: 'left', color: theme.text,
        fontFamily: designTokens.typography.fontFamily.display,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        borderRadius: '3px',
        opacity: loading ? 0.7 : 1,
      }}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{
          width: '6px', height: '6px', borderRadius: '50%',
          background: active ? color : theme.textTertiary, flexShrink: 0,
        }} />
        {label}
        {loading && <span style={{ fontSize: '9px', color: theme.textTertiary }}>...</span>}
        {active && count != null && (
          <span style={{ fontSize: '9px', color: theme.textTertiary, fontFamily: designTokens.typography.fontFamily.mono }}>
            {count.toLocaleString()}
          </span>
        )}
      </span>
      <span style={{
        fontFamily: designTokens.typography.fontFamily.mono,
        fontSize: '9px', fontWeight: 500,
        color: active ? designTokens.colors.brand : theme.textTertiary,
      }}>
        {active ? '[ON]' : '[--]'}
      </span>
    </button>
  );
};

const SectionHeader: React.FC<{
  title: string;
  collapsed?: boolean;
  onToggle?: () => void;
  count?: number;
  anyActive?: boolean;
  onToggleAll?: () => void;
}> = ({ title, collapsed, onToggle, count, anyActive, onToggleAll }) => {
  const { theme } = useTheme();
  const isCollapsible = onToggle != null;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', width: '100%',
      marginBottom: '8px', marginTop: '14px',
    }}>
      <button
        onClick={onToggle}
        style={{
          display: 'flex', alignItems: 'center', flex: 1,
          fontSize: '10px', fontWeight: 600, textTransform: 'uppercase',
          letterSpacing: '0.06em', color: theme.textSecondary, whiteSpace: 'nowrap',
          borderLeft: `3px solid ${designTokens.colors.brand}`,
          paddingLeft: '8px',
          background: 'none', border: 'none', cursor: isCollapsible ? 'pointer' : 'default',
          borderLeftWidth: '3px', borderLeftStyle: 'solid', borderLeftColor: designTokens.colors.brand,
        }}
      >
        {isCollapsible && (
          <ChevronRight size={10} style={{
            transition: 'transform 0.15s',
            transform: collapsed ? 'rotate(0deg)' : 'rotate(90deg)',
            marginRight: '4px', flexShrink: 0,
          }} />
        )}
        {title}
        {count != null && (
          <span style={{ marginLeft: 'auto', fontSize: '9px', fontWeight: 400, color: theme.textTertiary }}>
            {count}
          </span>
        )}
      </button>
      {onToggleAll && (
        <button
          onClick={e => { e.stopPropagation(); onToggleAll(); }}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            padding: '0 12px 0 8px',
            fontSize: '10px', fontWeight: 500, letterSpacing: '1px',
            color: anyActive ? designTokens.colors.brand : theme.textTertiary,
          }}
        >
          {anyActive ? '[ALL]' : '[--]'}
        </button>
      )}
    </div>
  );
};

const GroupHeader: React.FC<{
  title: string;
  collapsed?: boolean;
  onToggle?: () => void;
  active?: boolean;
  onToggleActive?: () => void;
}> = ({ title, collapsed, onToggle, active, onToggleActive }) => {
  const { theme } = useTheme();
  return (
    <div style={{
      display: 'flex', alignItems: 'center', width: '100%',
      paddingBottom: '6px', marginBottom: '4px', marginTop: '18px',
      borderBottomWidth: '1px', borderBottomStyle: 'solid' as const, borderBottomColor: theme.border,
    }}>
      <button
        onClick={onToggle}
        style={{
          display: 'flex', alignItems: 'center', flex: 1,
          fontSize: '11px', fontWeight: 600, textTransform: 'uppercase',
          letterSpacing: '0.08em', color: designTokens.colors.brand,
          background: 'none', border: 'none', cursor: 'pointer', padding: 0,
        }}
      >
        <ChevronRight size={10} style={{
          transition: 'transform 0.15s',
          transform: collapsed ? 'rotate(0deg)' : 'rotate(90deg)',
          marginRight: '4px', flexShrink: 0,
        }} />
        {title}
      </button>
      {onToggleActive && (
        <button
          onClick={onToggleActive}
          style={{
            fontFamily: designTokens.typography.fontFamily.mono,
            fontSize: '9px', fontWeight: 500,
            color: active ? designTokens.colors.brand : theme.textTertiary,
            background: 'none', border: 'none', cursor: 'pointer', padding: 0,
            flexShrink: 0,
          }}
        >
          {active ? '[ON]' : '[--]'}
        </button>
      )}
    </div>
  );
};

const NO_FOCUS: string[] = [];

// Mono bracket tag used as a small inline action: [ON] [--] [ONLY] [ZOOM] [×]
const MonoAction: React.FC<{ label: string; active?: boolean; title?: string; onClick: () => void }> = ({ label, active = true, title, onClick }) => {
  const { theme } = useTheme();
  return (
    <button
      onClick={e => { e.stopPropagation(); onClick(); }}
      title={title}
      style={{
        background: 'none', border: 'none', cursor: 'pointer', padding: '0 3px', flexShrink: 0,
        fontFamily: designTokens.typography.fontFamily.mono, fontSize: '9px',
        color: active ? designTokens.colors.brand : theme.textTertiary,
      }}
    >
      {label}
    </button>
  );
};

// One company or operator-name line in the company picker and the companies-in-view list.
const CompanyRow: React.FC<{
  label: string;
  detail: string;
  hint?: string;
  visible: boolean;
  bold?: boolean;
  indent?: boolean;
  chevron?: 'open' | 'closed';
  onRowClick: () => void;
  onOnly?: () => void;
  onToggleVisible?: () => void;
}> = ({ label, detail, hint, visible, bold, indent, chevron, onRowClick, onOnly, onToggleVisible }) => {
  const { theme } = useTheme();
  return (
    <div style={{ display: 'flex', alignItems: 'center', width: '100%', paddingLeft: indent ? '18px' : 0 }}>
      <button
        onClick={onRowClick}
        title={hint ? `${label} · ${hint}` : label}
        style={{
          flex: 1, minWidth: 0, padding: '3px 6px', fontSize: '11px', fontWeight: bold ? 600 : 400,
          background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left',
          color: visible ? theme.text : theme.textTertiary,
          fontFamily: designTokens.typography.fontFamily.display,
          display: 'flex', alignItems: 'center', gap: '4px',
        }}
      >
        {chevron && (
          <ChevronRight size={10} style={{ flexShrink: 0, transition: 'transform 0.15s', transform: chevron === 'open' ? 'rotate(90deg)' : 'none' }} />
        )}
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {label}
          {hint && <span style={{ fontWeight: 400, color: theme.textTertiary }}> · {hint}</span>}
        </span>
        <span style={{ fontFamily: designTokens.typography.fontFamily.mono, fontSize: '9px', color: theme.textTertiary, flexShrink: 0, whiteSpace: 'nowrap' }}>{detail}</span>
      </button>
      {onOnly && <MonoAction label="[ONLY]" title="Show only this company's lines" onClick={onOnly} />}
      {onToggleVisible && <MonoAction label={visible ? '[ON]' : '[--]'} active={visible} title={visible ? 'Hide' : 'Show'} onClick={onToggleVisible} />}
    </div>
  );
};

const EmptyNote: React.FC<{ text: string }> = ({ text }) => {
  const { theme } = useTheme();
  return <div style={{ padding: '4px 8px', fontSize: '10px', color: theme.textTertiary, fontFamily: designTokens.typography.fontFamily.display }}>{text}</div>;
};

const FilterLabel: React.FC<{ title: string }> = ({ title }) => {
  const { theme } = useTheme();
  return (
    <div style={{
      fontSize: '10px', fontWeight: 500, textTransform: 'uppercase',
      letterSpacing: '0.06em', color: theme.textTertiary,
      marginTop: '10px', marginBottom: '4px',
    }}>
      {title}
    </div>
  );
};

export const FilterSidebar: React.FC<FilterSidebarProps> = ({
  filters, onChange, pipelineFilters, onPipelineFiltersChange,
  showFederalLands, onToggleFederalLands,
  showCrownPineTracts, onToggleCrownPineTracts,
  showAssemblyGroups, onToggleAssemblyGroups,
  showGathering, onToggleGathering,
  showGasTransmission, onToggleGasTransmission,
  showLiquidsTransmission, onToggleLiquidsTransmission,
  anyPipelineActive, onToggleAllPipelines,
  showDesertSw, onToggleDesertSw,
  operators, hiddenOperators, onToggleOperator, onSetOperatorsHidden, onShowAllOperators, operatorCatalog, onFitBounds,
  showPowerInfra, onTogglePowerInfra, powerLoading,
  showOsmSwitchyards, onToggleOsmSwitchyards, osmSwitchyardsHifldNewOnly, onToggleOsmSwitchyardsHifldNewOnly, osmSwitchyardsLoading,
  showGridCapacity, onToggleGridCapacity, gridCapacityLoading, gridCapacityTiers, onToggleGridCapacityTier,
  showQueueHeatmap, onToggleQueueHeatmap, queueHeatmapLoading,
  showCongestionHeatmap, onToggleCongestionHeatmap, congestionHeatmapLoading,
  showElectricTerritories, onToggleElectricTerritories,
  showAirportZones, onToggleAirportZones, airportLoading, airportFilter, onAirportFilterChange,
  showParcels, onToggleParcels,
  showFiber, onToggleFiber, fiberLoading,
  showSegra, onToggleSegra, showZayo, onToggleZayo, showFiberLight, onToggleFiberLight, showNtiaMiddleMile, onToggleNtiaMiddleMile,
  showFiberRowPipelines, onToggleFiberRowPipelines,
  showIxpFacilities, onToggleIxpFacilities,
  showWater, onToggleWater, waterLoading,
  showDataCenters, onToggleDataCenters, dcLoading,
  showFloodZones, onToggleFloodZones, floodZoneLoading,
  showTwdbFloodHazards, onToggleTwdbFloodHazards, twdbFloodHazardsLoading,
  showHighways, onToggleHighways, highwayLoading,
  showRoads, onToggleRoads, roadsLoading,
  showOsmPipelineCandidates, onToggleOsmPipelineCandidates, osmPipelineCandidatesLoading,
  memphisLayers, onMemphisLayersChange, onMemphisZoom, onToggleAllMemphis,
  etLayers, etData = null, etLoading = false, etError = null, onEtLoad, onEtLayersChange, onEtZoom, onToggleAllEt,
  showGeorgiaSites, onToggleGeorgiaSites, showGeorgiaEvidence, onToggleGeorgiaEvidence, anyGeorgiaActive, onToggleAllGeorgia, onZoomToGeorgiaSite,
  showRailroads, onToggleRailroads, railroadLoading,
  showGasWells, onToggleGasWells, showOilWells, onToggleOilWells, wellsLoading,
  showStateTrustLands, onToggleStateTrustLands, trustLandLoading, showProtectedAreas, onToggleProtectedAreas,
  showOperatorAois, onToggleOperatorAois,
  showLandListings, onToggleLandListings, listingsLoading,
  showGasPlants, onToggleGasPlants, gasPlantLoading,
  showCompressors, onToggleCompressors,
  showReceiptDelivery, onToggleReceiptDelivery,
  showInterconnects, onToggleInterconnects,
  showGasStorage, onToggleGasStorage,
  gasInfraLoading, anyGasSupplyActive, onToggleAllGasSupply,
  showEpaZones, onToggleEpaZones, epaZoneLoading,
  showEjScreen, onToggleEjScreen, ejScreenLoading,
  showCriticalHabitat, onToggleCriticalHabitat,
  showWetlands, onToggleWetlands,
  showSeismicRisk, onToggleSeismicRisk,
  showUrbanAreas, onToggleUrbanAreas,
  showWildfire, onToggleWildfire, wildfireLoading,
  showMineSubsidence, onToggleMineSubsidence,
  showAmlSites, onToggleAmlSites,
  anyFiberActive, onToggleAllFiber,
  anyInfraActive, onToggleAllInfra,
  anyWaterActive, onToggleAllWater,
  showTwdbWells, onToggleTwdbWells,
  showBrackish, onToggleBrackish,
  showTdsSamples, onToggleTdsSamples,
  showWaterRegulatoryZones, onToggleWaterRegulatoryZones,
  anyEnvActive, onToggleAllEnv,
  anyLandActive, onToggleAllLand,
  showSlopeDrape, onToggleSlopeDrape, showSlope1m, onToggleSlope1m,
  slopeDrapeOpacity, onSlopeDrapeOpacityChange,
  activeLayerCount, onResetAllLayers,
  showSavedLocations,
  onToggleSavedLocations,
  savedLocations,
  savedLocationsLoading,
  onClickSavedLocation,
  onRenameSavedLocation,
  onDeleteSavedLocation,
}) => {
  const { theme } = useTheme();
  const [searchText, setSearchText] = useState(filters.search || '');
  const [showCompanies, setShowCompanies] = useState(false);
  const [companyQuery, setCompanyQuery] = useState('');
  const [expandedCompanies, setExpandedCompanies] = useState<Set<string>>(new Set());
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set(['pipelineFilters', 'parcelFilters', 'parcelAdvanced', 'oneOffs', 'memphis', 'georgia', 'etResearch']));

  const toggleSection = useCallback((section: string) => {
    setCollapsedSections(prev => {
      const next = new Set(prev);
      next.has(section) ? next.delete(section) : next.add(section);
      return next;
    });
  }, []);

  const updateFilter = useCallback((key: keyof Filters, value: string | number | boolean | undefined) => {
    const next = { ...filters };
    if (value === undefined || value === '') {
      delete next[key];
    } else {
      (next as Record<string, unknown>)[key] = value;
    }
    onChange(next);
  }, [filters, onChange]);

  const handleSearch = useCallback(() => {
    updateFilter('search', searchText || undefined);
  }, [searchText, updateFilter]);

  const handleSearchKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  }, [handleSearch]);

  // ── Company focus ("only this company's lines") ──
  // The chosen company and its operator names travel in pipelineFilters, so the existing
  // refetch path carries the names to the server; hide toggles stay client-side.
  const focusOperators = pipelineFilters.operators ?? NO_FOCUS;
  const catalogByOperator = useMemo(() => new Map(operatorCatalog.map(e => [e.operator, e])), [operatorCatalog]);
  const focusCompany = useCallback((company: string | null, inViewMembers: string[] = []) => {
    const next = { ...pipelineFilters };
    delete next.company;
    delete next.operators;
    if (company) {
      const names = operatorsForCompany(operatorCatalog, company);
      const ops = names.length ? names : inViewMembers;
      if (ops.length) { next.company = company; next.operators = ops; }
    }
    onPipelineFiltersChange(next);
    setCompanyQuery('');
    if (company && !anyPipelineActive) onToggleGasTransmission();
  }, [pipelineFilters, operatorCatalog, onPipelineFiltersChange, anyPipelineActive, onToggleGasTransmission]);
  const toggleExpandedCompany = useCallback((company: string) => {
    setExpandedCompanies(prev => {
      const next = new Set(prev);
      next.has(company) ? next.delete(company) : next.add(company);
      return next;
    });
  }, []);
  const summary = useMemo(
    () => focusSummary(pipelineFilters.company, focusOperators, catalogByOperator),
    [pipelineFilters.company, focusOperators, catalogByOperator],
  );
  const focusBbox = useMemo(
    () => unionBbox(focusOperators.map(op => catalogByOperator.get(op)?.bbox ?? null)),
    [focusOperators, catalogByOperator],
  );
  const companyHits = useMemo(() => searchCompanies(operatorCatalog, companyQuery, 20), [operatorCatalog, companyQuery]);
  const companyGroups = useMemo(() => groupByCompany(operators, catalogByOperator), [operators, catalogByOperator]);

  const clearFilters = useCallback(() => {
    setSearchText('');
    onChange({});
    onPipelineFiltersChange({});
  }, [onChange, onPipelineFiltersChange]);

  const hasFilters = Object.keys(filters).length > 0 ||
    Object.values(pipelineFilters).some(v => v !== undefined && v !== '');

  return (
    <div style={{
      width: designTokens.layout.sidebarWidth, background: theme.backgroundSecondary,
      borderRight: `1px solid ${theme.border}`,
      display: 'flex', flexDirection: 'column', overflow: 'hidden', flexShrink: 0,
    }}>
      {/* Scrollable content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '12px' }}>
        {/* ── Pipeline Filters ── */}
        <GroupHeader title="Pipeline Filters" collapsed={collapsedSections.has('pipelineFilters')} onToggle={() => toggleSection('pipelineFilters')} active={anyPipelineActive} onToggleActive={onToggleAllPipelines} />
        {!collapsedSections.has('pipelineFilters') && (<>
        {/* Pipeline Type chips */}
        <FilterLabel title="Pipeline Type" />
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
          <FilterChip label="Gas Trans." active={showGasTransmission} color={designTokens.colors.pipelineGasTransmission} onClick={onToggleGasTransmission} />
          <FilterChip label="Gathering" active={showGathering} color={designTokens.colors.pipelineGathering} onClick={onToggleGathering} />
          <FilterChip label="Liquids" active={showLiquidsTransmission} color={designTokens.colors.pipelineLiquidsTransmission} onClick={onToggleLiquidsTransmission} />
          <FilterChip label="Desert SW" active={showDesertSw} color="#C4705A" onClick={onToggleDesertSw} />
        </div>

        {/* Company focus: pick a company, see only its lines statewide */}
        <FilterLabel title="Company" />
        {summary && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 8px', marginBottom: '6px',
            borderRadius: '4px', background: designTokens.colors.brand + '14', border: `1px solid ${designTokens.colors.brand}40`,
            fontSize: '11px', color: theme.text, fontFamily: designTokens.typography.fontFamily.display,
          }}>
            <span style={{ fontSize: '9px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: designTokens.colors.brand, flexShrink: 0 }}>Only</span>
            <span title={focusOperators.join('\n')} style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {summary.label} · {summary.names} {summary.names === 1 ? 'name' : 'names'}{summary.jv ? ` · ${summary.jv} JV` : ''}
            </span>
            {focusBbox && <MonoAction label="[ZOOM]" title="Fit the map to these lines" onClick={() => onFitBounds(focusBbox)} />}
            <MonoAction label="[×]" title="Clear focus" onClick={() => focusCompany(null)} />
          </div>
        )}
        <input
          value={companyQuery}
          onChange={e => setCompanyQuery(e.target.value)}
          placeholder="Search companies..."
          style={{
            width: '100%', padding: '6px 8px', marginBottom: '6px',
            border: `1px solid ${theme.border}`, borderRadius: '4px',
            fontSize: '12px', boxSizing: 'border-box' as const,
            fontFamily: designTokens.typography.fontFamily.display,
            background: theme.inputBg, color: theme.text,
          }}
        />
        {companyQuery.trim() && (
          <div style={{ maxHeight: '220px', overflowY: 'auto', marginBottom: '6px' }}>
            {companyHits.map(h => (
              <CompanyRow
                key={h.company}
                label={h.company}
                hint={h.via ? `via ${h.via}` : undefined}
                detail={`${h.names} ${h.names === 1 ? 'name' : 'names'}`}
                visible
                bold
                onRowClick={() => focusCompany(h.company)}
                onOnly={() => focusCompany(h.company)}
              />
            ))}
            {companyHits.length === 0 && (
              <EmptyNote text={operatorCatalog.length ? 'No company matches.' : 'Loading companies...'} />
            )}
          </div>
        )}

        {/* Pipeline Capacity */}
        <SectionHeader title="Pipeline Capacity" collapsed={collapsedSections.has('capacity')} onToggle={() => toggleSection('capacity')} />
        {!collapsedSections.has('capacity') && (
          <>
            <div style={{ marginBottom: '8px', fontSize: '10px', lineHeight: 1.45, color: theme.textTertiary }}>
              Power-equivalent and inferred delivery thresholds are unavailable while native operator observations are unverified. Use diameter and source selectors below for physical and provenance context.
            </div>
            <div>
              <FilterLabel title="Capacity Source Dataset" />
              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                {[
                  { label: 'All', value: '' as const },
                  { label: 'EIA Source', value: 'eia_rated' as const, color: '#16a34a' },
                  { label: 'Diameter Est.', value: 'diameter_estimate' as const, color: '#ca8a04' },
                ].map(t => {
                  const presets = ['eia_rated', 'diameter_estimate'];
                  const active = t.value === ''
                    ? !presets.includes(pipelineFilters.capacity_source as string)
                    : pipelineFilters.capacity_source === t.value;
                  return (
                    <FilterChip
                      key={t.label}
                      label={t.label}
                      active={active}
                      color={t.color || theme.textSecondary}
                      onClick={() => onPipelineFiltersChange({ ...pipelineFilters, capacity_source: t.value || undefined })}
                    />
                  );
                })}
              </div>
            </div>
          </>
        )}

        {/* Companies in view: in-view names grouped by company, with hide toggles */}
        {anyPipelineActive && (
          <>
            <SectionHeader
              title="Companies in view"
              collapsed={!showCompanies}
              onToggle={() => setShowCompanies(!showCompanies)}
              count={companyGroups.length}
              anyActive={hiddenOperators.size === 0}
              onToggleAll={hiddenOperators.size > 0 ? onShowAllOperators : undefined}
            />
            {showCompanies && (
              <div style={{ maxHeight: '260px', overflowY: 'auto' }}>
                {companyGroups.map(g => {
                  const names = g.members.map(m => m.name);
                  const allVisible = names.every(n => !hiddenOperators.has(n));
                  const single = g.members.length === 1 && g.members[0].name === g.company;
                  const open = expandedCompanies.has(g.company);
                  return (
                    <React.Fragment key={g.company}>
                      <CompanyRow
                        label={g.company}
                        detail={single ? `${g.count} seg` : `${g.members.length} names · ${g.count} seg`}
                        visible={allVisible}
                        bold={!single}
                        chevron={single ? undefined : open ? 'open' : 'closed'}
                        onRowClick={() => (single ? onToggleOperator(g.company) : toggleExpandedCompany(g.company))}
                        onOnly={() => focusCompany(g.company, names)}
                        onToggleVisible={() => onSetOperatorsHidden(names, allVisible)}
                      />
                      {!single && open && g.members.map(m => (
                        <CompanyRow
                          key={m.name}
                          indent
                          label={m.name}
                          hint={m.jvWith.length ? `JV with ${m.jvWith.join(', ')}` : undefined}
                          detail={`${m.count} seg`}
                          visible={!hiddenOperators.has(m.name)}
                          onRowClick={() => onToggleOperator(m.name)}
                          onToggleVisible={() => onToggleOperator(m.name)}
                        />
                      ))}
                    </React.Fragment>
                  );
                })}
                {companyGroups.length === 0 && (
                  <EmptyNote text={focusOperators.length ? 'No focused lines in view. Try [ZOOM].' : 'No pipelines in view.'} />
                )}
              </div>
            )}
          </>
        )}

        </>)}

        {/* ── Parcel Filters ── */}
        <GroupHeader title="Parcel Filters" collapsed={collapsedSections.has('parcelFilters')} onToggle={() => toggleSection('parcelFilters')} active={showParcels} onToggleActive={onToggleParcels} />
        {!collapsedSections.has('parcelFilters') && (
          <>
            {/* Primary: Acreage */}
            <FilterLabel title="Acreage" />
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
              {[
                { label: '5+ ac', value: 5 },
                { label: '200+ ac', value: 200 },
                { label: '500+ ac', value: 500 },
                { label: '1000+ ac', value: 1000 },
                { label: '2000+ ac', value: 2000 },
                { label: 'All', value: 0 },
              ].map(t => {
                const active = (filters.min_acreage ?? 200) === t.value;
                return (
                  <FilterChip
                    key={t.label}
                    label={t.label}
                    active={active}
                    color={designTokens.colors.brand}
                    onClick={() => {
                      const next = { ...filters };
                      delete next.max_acreage;
                      next.min_acreage = t.value;
                      onChange(next);
                    }}
                  />
                );
              })}
            </div>

            {/* Primary: Acquisition Score */}
            <FilterLabel title="Acquisition Score" />
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
              {[
                { tier: 'All', min: undefined as number | undefined },
                { tier: 'WARM', min: 26 },
                { tier: 'HOT', min: 51 },
                { tier: 'URGENT', min: 76 },
              ].map(t => {
                const isAll = t.min === undefined;
                const active = isAll
                  ? filters.min_score === undefined
                  : filters.min_score === t.min && filters.max_score === undefined;
                const color = isAll ? theme.textSecondary : getScoreColor(t.min!, 999);
                return (
                  <FilterChip
                    key={t.tier}
                    label={`${t.tier}${!isAll ? '+' : ''}`}
                    active={active}
                    color={color}
                    onClick={() => {
                      const next = { ...filters };
                      delete next.min_score;
                      delete next.max_score;
                      if (!isAll) next.min_score = t.min;
                      onChange(next);
                    }}
                  />
                );
              })}
            </div>

            {/* Primary: County */}
            <FilterLabel title="County" />
            <select
              value={filters.county || ''}
              onChange={e => updateFilter('county', e.target.value || undefined)}
              style={{
                width: '100%', padding: '6px 8px',
                border: `1px solid ${theme.border}`, borderRadius: '4px',
                fontSize: '12px', boxSizing: 'border-box' as const,
                fontFamily: designTokens.typography.fontFamily.display,
                background: theme.inputBg, color: theme.text,
              }}
            >
              <option value="">All Counties</option>
              {COUNTIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>

            {/* Advanced filters (collapsed by default) */}
            <SectionHeader title="Advanced" collapsed={collapsedSections.has('parcelAdvanced')} onToggle={() => toggleSection('parcelAdvanced')} />
            {!collapsedSections.has('parcelAdvanced') && (
              <>
                {/* Max Avg Slope % */}
                <FilterLabel title="Max Avg Slope %" />
                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                  {[
                    { label: 'Any', value: undefined as number | undefined, color: theme.textSecondary },
                    { label: '<2% Ideal', value: 2, color: '#16a34a' },
                    { label: '<5% OK', value: 5, color: '#ca8a04' },
                  ].map(t => {
                    const presets = [2, 5];
                    const active = t.value === undefined
                      ? !presets.includes(filters.max_slope as number)
                      : filters.max_slope === t.value;
                    return (
                      <FilterChip
                        key={t.label}
                        label={t.label}
                        active={active}
                        color={t.color}
                        onClick={() => updateFilter('max_slope', t.value)}
                      />
                    );
                  })}
                </div>

                {/* Max Highway Dist */}
                <FilterLabel title="Max Highway Dist" />
                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                  {[
                    { label: 'Any', value: undefined as number | undefined, color: theme.textSecondary },
                    { label: '<2 mi', value: 2, color: '#16a34a' },
                    { label: '<5 mi', value: 5, color: '#ca8a04' },
                    { label: '<10 mi', value: 10, color: '#dc2626' },
                  ].map(t => {
                    const presets = [2, 5, 10];
                    const active = t.value === undefined
                      ? !presets.includes(filters.max_highway_miles as number)
                      : filters.max_highway_miles === t.value;
                    return (
                      <FilterChip
                        key={t.label}
                        label={t.label}
                        active={active}
                        color={t.color}
                        onClick={() => updateFilter('max_highway_miles', t.value)}
                      />
                    );
                  })}
                </div>

                {/* Max Substation Dist */}
                <FilterLabel title="Max Substation Dist" />
                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                  {[
                    { label: 'Any', value: undefined as number | undefined, color: theme.textSecondary },
                    { label: '<5 mi', value: 5, color: '#16a34a' },
                    { label: '<10 mi', value: 10, color: '#ca8a04' },
                    { label: '<25 mi', value: 25, color: '#dc2626' },
                  ].map(t => {
                    const presets = [5, 10, 25];
                    const active = t.value === undefined
                      ? !presets.includes(filters.max_substation_miles as number)
                      : filters.max_substation_miles === t.value;
                    return (
                      <FilterChip
                        key={t.label}
                        label={t.label}
                        active={active}
                        color={t.color}
                        onClick={() => updateFilter('max_substation_miles', t.value)}
                      />
                    );
                  })}
                </div>

                {/* Grid Capacity (75 MW substation voltage) */}
                <FilterLabel title="Min Substation Voltage" />
                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                  {[
                    { label: 'Any', value: undefined, color: '#8A9CAA' },
                    { label: '≥138 kV', value: 138, color: '#d97706' },
                    { label: '≥230 kV', value: 230, color: '#0891b2' },
                    { label: '≥345 kV', value: 345, color: '#16a34a' },
                  ].map(t => {
                    const active = t.value === undefined
                      ? !filters.min_substation_kv
                      : filters.min_substation_kv === t.value;
                    return (
                      <FilterChip
                        key={t.label}
                        label={t.label}
                        active={active}
                        color={t.color}
                        onClick={() => updateFilter('min_substation_kv', t.value)}
                      />
                    );
                  })}
                </div>

                {/* Fiber Connectivity */}
                <FilterLabel title="Fiber" />
                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                  {[
                    { label: 'Any', value: undefined, color: '#8A9CAA' },
                    { label: 'Enterprise', value: 'enterprise', color: '#16a34a' },
                    { label: '2+ Providers', value: '2prov', color: '#0891b2' },
                  ].map(t => {
                    const active = t.value === undefined
                      ? !filters.fiber_enterprise && !filters.min_fiber_providers
                      : t.value === 'enterprise' ? !!filters.fiber_enterprise
                      : !!filters.min_fiber_providers;
                    return (
                      <FilterChip
                        key={t.label}
                        label={t.label}
                        active={active}
                        color={t.color}
                        onClick={() => {
                          if (t.value === undefined) {
                            updateFilter('fiber_enterprise', undefined);
                            updateFilter('min_fiber_providers', undefined);
                          } else if (t.value === 'enterprise') {
                            updateFilter('fiber_enterprise', filters.fiber_enterprise ? undefined : true);
                            updateFilter('min_fiber_providers', undefined);
                          } else {
                            updateFilter('fiber_enterprise', undefined);
                            updateFilter('min_fiber_providers', filters.min_fiber_providers ? undefined : 2);
                          }
                        }}
                      />
                    );
                  })}
                </div>

                {/* Owner Search */}
                <FilterLabel title="Owner Search" />
                <div style={{ display: 'flex', gap: '4px' }}>
                  <input
                    value={searchText}
                    onChange={e => setSearchText(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    placeholder="Search owner name..."
                    style={{
                      width: '100%', padding: '6px 8px',
                      border: `1px solid ${theme.border}`, borderRadius: '4px',
                      fontSize: '12px', boxSizing: 'border-box' as const,
                      fontFamily: designTokens.typography.fontFamily.display,
                      background: theme.inputBg, color: theme.text,
                    }}
                  />
                  <button onClick={handleSearch} style={{
                    padding: '6px', border: `1px solid ${theme.border}`, borderRadius: '4px',
                    background: theme.inputBg, cursor: 'pointer', display: 'flex', alignItems: 'center',
                    color: theme.textSecondary,
                  }}>
                    <Search size={12} />
                  </button>
                </div>
              </>
            )}

            {/* Clear filters */}
            {hasFilters && (
              <button onClick={clearFilters} style={{
                width: '100%', padding: '6px', background: 'transparent',
                border: `1px solid ${theme.border}`, borderRadius: '4px',
                fontSize: '11px', color: theme.textSecondary, cursor: 'pointer',
                fontFamily: designTokens.typography.fontFamily.display,
                marginTop: '14px',
              }}>
                Clear All Filters
              </button>
            )}
          </>
        )}

        {/* ── Map Layers ── */}
        <GroupHeader title="Map Layers" collapsed={collapsedSections.has('layers')} onToggle={() => toggleSection('layers')} />
        {!collapsedSections.has('layers') && (
          <>
            {etLayers && onEtLayersChange && onEtZoom && onEtLoad && onToggleAllEt && <>
              <SectionHeader title="Energy Transfer" collapsed={collapsedSections.has('etResearch')}
                onToggle={() => { if (collapsedSections.has('etResearch')) onEtLoad(); toggleSection('etResearch'); }}
                anyActive={etLayers.powergen || etLayers.intrastate} onToggleAll={onToggleAllEt} />
              {!collapsedSections.has('etResearch') && <EtResearchSection layers={etLayers}
                onChange={onEtLayersChange} onZoom={onEtZoom} data={etData}
                loading={etLoading} error={etError} onRetry={onEtLoad} />}
            </>}
            {/* Gas Supply & Logistics */}
            <SectionHeader title="Gas Supply & Logistics" collapsed={collapsedSections.has('layersGasSupply')} onToggle={() => toggleSection('layersGasSupply')} anyActive={anyGasSupplyActive} onToggleAll={onToggleAllGasSupply} />
            {!collapsedSections.has('layersGasSupply') && (
              <>
                <ToggleRow label="Operator AOIs" active={showOperatorAois} onToggle={onToggleOperatorAois} color="#0f766e" />
                {showOperatorAois && (
                  <div style={{ marginTop: '2px', marginLeft: '22px', marginBottom: '4px' }}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                      {[
                        { label: 'AOI Boundary', color: '#0f766e' },
                        { label: 'Phase 2', color: '#1d4ed8' },
                        { label: 'Phase 3', color: '#b45309' },
                      ].map(c => (
                        <span key={c.label} style={{ fontSize: '9px', color: c.color, fontWeight: 600, display: 'flex', alignItems: 'center', gap: '3px' }}>
                          <span style={{ width: '10px', height: '2px', background: c.color }} />
                          {c.label}
                        </span>
                      ))}
                      <span style={{ fontSize: '9px', color: '#475569', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '3px' }}>
                        <span style={{ width: '10px', height: '0', borderTop: '2px dashed #475569' }} />
                        10-mi Buffer
                      </span>
                    </div>
                    <div style={{ fontSize: '9px', color: '#888', marginTop: '2px' }}>Partner-deck supply zones · operator-supplied phases · BSM AOI outers pending</div>
                    <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px dashed #e5e7eb' }}>
                      <div style={{ fontSize: 10, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Supply Zone Briefings</div>
                      <ZoneListLinks />
                    </div>
                  </div>
                )}
                <ToggleRow label="Gas Processing Plants" active={showGasPlants} onToggle={onToggleGasPlants} color="#f97316" loading={gasPlantLoading} />
                <ToggleRow label="Compressor Stations" active={showCompressors} onToggle={onToggleCompressors} color={designTokens.colors.gasInfraCompressor} loading={gasInfraLoading} />
                <ToggleRow label="Receipt/Delivery Pts" active={showReceiptDelivery} onToggle={onToggleReceiptDelivery} color={designTokens.colors.gasInfraReceiptDelivery} loading={gasInfraLoading} />
                <ToggleRow label="Interconnects" active={showInterconnects} onToggle={onToggleInterconnects} color={designTokens.colors.gasInfraInterconnect} loading={gasInfraLoading} />
                <ToggleRow label="Gas Storage" active={showGasStorage} onToggle={onToggleGasStorage} color={designTokens.colors.gasInfraStorage} loading={gasInfraLoading} />
                <div style={{ borderTop: `1px solid ${theme.border}`, marginTop: '4px', paddingTop: '4px' }}>
                  <ToggleRow label="Gas Wells" active={showGasWells} onToggle={onToggleGasWells} color="#16a34a" loading={wellsLoading} />
                  <ToggleRow label="Oil Wells" active={showOilWells} onToggle={onToggleOilWells} color="#d97706" loading={wellsLoading} />
                </div>
              </>
            )}

            {/* Infrastructure */}
            <SectionHeader title="Fiber & Connectivity" collapsed={collapsedSections.has('layersFiber')} onToggle={() => toggleSection('layersFiber')} anyActive={anyFiberActive} onToggleAll={onToggleAllFiber} />
            {!collapsedSections.has('layersFiber') && (
              <>
                <ToggleRow label="Fiber Coverage" active={showFiber} onToggle={onToggleFiber} color="#8b5cf6" loading={fiberLoading} />
                <ToggleRow label="Segra" active={showSegra} onToggle={onToggleSegra} color="#10b981" />
                <ToggleRow label="Zayo" active={showZayo} onToggle={onToggleZayo} color="#0891b2" />
                <ToggleRow label="FiberLight" active={showFiberLight} onToggle={onToggleFiberLight} color="#f59e0b" />
                <ToggleRow label="NTIA Middle Mile" active={showNtiaMiddleMile} onToggle={onToggleNtiaMiddleMile} color="#e11d48" />
                <ToggleRow label="Pipeline Fiber ROW" active={showFiberRowPipelines} onToggle={onToggleFiberRowPipelines} color="#7c3aed" />
                <ToggleRow label="IXPs & Colo Facilities" active={showIxpFacilities} onToggle={onToggleIxpFacilities} color="#10b981" />
              </>
            )}

            <SectionHeader title="Infrastructure" collapsed={collapsedSections.has('layersInfra')} onToggle={() => toggleSection('layersInfra')} anyActive={anyInfraActive} onToggleAll={onToggleAllInfra} />
            {!collapsedSections.has('layersInfra') && (
              <>
                <ToggleRow label="Airport Zones (100 mi)" active={showAirportZones} onToggle={onToggleAirportZones} color="#ef4444" loading={airportLoading} />
                {showAirportZones && (
                  <div style={{ display: 'flex', gap: '4px', marginTop: '4px', marginLeft: '22px' }}>
                    {([
                      { label: 'Commercial Only', value: 'commercial' as const },
                      { label: 'All Airports', value: 'all' as const },
                    ]).map(t => (
                      <FilterChip
                        key={t.value}
                        label={t.label}
                        active={airportFilter === t.value}
                        color="#ef4444"
                        onClick={() => onAirportFilterChange(t.value)}
                      />
                    ))}
                  </div>
                )}
                <ToggleRow label="Highways" active={showHighways} onToggle={onToggleHighways} color={designTokens.colors.highway} loading={highwayLoading} />
                <ToggleRow label="Roads" active={showRoads} onToggle={onToggleRoads} color="#a5b4fc" loading={roadsLoading} />
                <div style={{ marginTop: '2px', marginLeft: '22px', marginBottom: '4px', fontSize: '9px', color: '#888' }}>
                  FM, state and county roads · La Salle + 6 East Texas counties
                </div>
                <ToggleRow label="Railroads" active={showRailroads} onToggle={onToggleRailroads} color="#78716c" loading={railroadLoading} />
                <ToggleRow label="Power Infrastructure" active={showPowerInfra} onToggle={onTogglePowerInfra} color="#f59e0b" loading={powerLoading} />
                <ToggleRow label="OSM Switchyards" active={showOsmSwitchyards} onToggle={onToggleOsmSwitchyards} color="#10b981" loading={osmSwitchyardsLoading} />
                {showOsmSwitchyards && (
                  <div style={{ display: 'flex', gap: '4px', marginTop: '4px', marginLeft: '22px' }}>
                    <FilterChip
                      label="HIFLD-missing only"
                      active={osmSwitchyardsHifldNewOnly}
                      color="#10b981"
                      onClick={onToggleOsmSwitchyardsHifldNewOnly}
                    />
                  </div>
                )}
                <ToggleRow label="Grid Capacity (75 MW)" active={showGridCapacity} onToggle={onToggleGridCapacity} color="#16a34a" loading={gridCapacityLoading} />
                {showGridCapacity && (
                  <div style={{ display: 'flex', gap: '4px', marginTop: '4px', marginLeft: '22px' }}>
                    {[
                      { key: 'high', label: 'High', color: '#16a34a' },
                      { key: 'likely', label: 'Likely', color: '#0891b2' },
                      { key: 'moderate', label: 'Mod', color: '#d97706' },
                      { key: 'low', label: 'Low', color: '#6A7E8E' },
                    ].map(c => (
                      <FilterChip key={c.key} label={c.label} active={gridCapacityTiers.has(c.key)} color={c.color} onClick={() => onToggleGridCapacityTier(c.key)} />
                    ))}
                  </div>
                )}
                <ToggleRow label="ERCOT Queue Congestion" active={showQueueHeatmap} onToggle={onToggleQueueHeatmap} color="#ef4444" loading={queueHeatmapLoading} />
                <ToggleRow label="Transmission Congestion" active={showCongestionHeatmap} onToggle={onToggleCongestionHeatmap} color="#f97316" loading={congestionHeatmapLoading} />
                <ToggleRow label="Electric Territories" active={showElectricTerritories} onToggle={onToggleElectricTerritories} color="#d3102f" />
                {showElectricTerritories && (
                  <div style={{ display: 'flex', gap: '10px', marginTop: '4px', marginLeft: '22px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '9px', color: '#d3102f', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '3px' }}>
                      <span style={{ width: '12px', height: '0', borderTop: '2px solid #d3102f' }} />
                      IOU
                    </span>
                    <span style={{ fontSize: '9px', color: '#5a8cb2', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '3px' }}>
                      <span style={{ width: '8px', height: '8px', borderRadius: '2px', background: '#64b5f366', border: '1px solid #6e6e6e' }} />
                      Coop
                    </span>
                    <span style={{ fontSize: '9px', color: '#b45309', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '3px' }}>
                      <span style={{ width: '12px', height: '0', borderTop: '2px dashed #b45309' }} />
                      Muni
                    </span>
                  </div>
                )}
              </>
            )}

            {/* Water */}
            <SectionHeader title="Water" collapsed={collapsedSections.has('layersWater')} onToggle={() => toggleSection('layersWater')} anyActive={anyWaterActive} onToggleAll={onToggleAllWater} />
            {!collapsedSections.has('layersWater') && (
              <>
                <ToggleRow label="Water Infrastructure & Aquifers" active={showWater} onToggle={onToggleWater} color="#06b6d4" loading={waterLoading} />
                <ToggleRow label="TWDB Water Wells" active={showTwdbWells} onToggle={onToggleTwdbWells} color="#0ea5e9" />
                <ToggleRow label="Brackish Aquifer Extent" active={showBrackish} onToggle={onToggleBrackish} color="#14b8a6" />
                <ToggleRow label="Water Quality (TDS)" active={showTdsSamples} onToggle={onToggleTdsSamples} color="#0f766e" />
                {showTdsSamples && (
                  <div style={{ display: 'flex', gap: '8px', marginTop: '4px', marginLeft: '22px', flexWrap: 'wrap' }}>
                    {[
                      { label: '<1k fresh', color: '#3b82f6' },
                      { label: '1–3k', color: '#06b6d4' },
                      { label: '3–10k', color: '#14b8a6' },
                      { label: '10–35k', color: '#f59e0b' },
                      { label: '>35k', color: '#ef4444' },
                    ].map(c => (
                      <span key={c.label} style={{ fontSize: '9px', color: c.color, fontWeight: 600, display: 'flex', alignItems: 'center', gap: '3px' }}>
                        <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: c.color }} />
                        {c.label}
                      </span>
                    ))}
                  </div>
                )}
                <ToggleRow label="Water Regulatory Zones" active={showWaterRegulatoryZones} onToggle={onToggleWaterRegulatoryZones} color="#0284c7" />
                {showWaterRegulatoryZones && (
                  <div style={{ display: 'flex', gap: '8px', marginTop: '4px', marginLeft: '22px', flexWrap: 'wrap' }}>
                    {[
                      { label: 'GCD (TX)', color: '#0284c7' },
                      { label: 'AMA (AZ)', color: '#d97706' },
                      { label: 'NM Basin', color: '#b85c5c' },
                      { label: 'NM Closure', color: '#dc2626' },
                    ].map(c => (
                      <span key={c.label} style={{ fontSize: '9px', color: c.color, fontWeight: 600, display: 'flex', alignItems: 'center', gap: '3px' }}>
                        <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: c.color }} />
                        {c.label}
                      </span>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Environmental & Permitting */}
            <SectionHeader title="Environmental & Permitting" collapsed={collapsedSections.has('layersEnv')} onToggle={() => toggleSection('layersEnv')} anyActive={anyEnvActive} onToggleAll={onToggleAllEnv} />
            {!collapsedSections.has('layersEnv') && (
              <>
                <ToggleRow label="EPA Nonattainment" active={showEpaZones} onToggle={onToggleEpaZones} color="#ef4444" loading={epaZoneLoading} />
                <ToggleRow label="EJ Screen" active={showEjScreen} onToggle={onToggleEjScreen} color="#f59e0b" loading={ejScreenLoading} />
                {showEjScreen && (
                  <div style={{ marginTop: '2px', marginLeft: '22px', marginBottom: '4px' }}>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                      {[
                        { label: '50th', color: '#f59e0b' },
                        { label: '80th', color: '#f97316' },
                        { label: '90th+', color: '#ef4444' },
                      ].map(c => (
                        <span key={c.label} style={{ fontSize: '9px', color: c.color, fontWeight: 600, display: 'flex', alignItems: 'center', gap: '3px' }}>
                          <span style={{ width: '6px', height: '6px', borderRadius: '2px', background: c.color }} />
                          {c.label}
                        </span>
                      ))}
                    </div>
                    <div style={{ fontSize: '9px', color: '#888', marginTop: '2px' }}>EJ burden percentile · click block for details</div>
                  </div>
                )}
                <ToggleRow label="FEMA Flood Zones" active={showFloodZones} onToggle={onToggleFloodZones} color={designTokens.colors.floodZone100yr} loading={floodZoneLoading} />
                <ToggleRow label="TWDB Existing 1% Hazard" active={showTwdbFloodHazards} onToggle={onToggleTwdbFloodHazards} color="#2563eb" loading={twdbFloodHazardsLoading} />
                {showTwdbFloodHazards && (
                  <div style={{ fontSize: '9px', color: '#888', margin: '2px 8px 4px 22px', lineHeight: 1.35 }}>
                    Targeted coverage: Frio + 20 Crown Pine ETX counties. TWDB planning compilation; may include modeled and regulatory source data; not itself a FEMA flood-zone determination.
                  </div>
                )}
                <ToggleRow label="Protected Areas" active={showProtectedAreas} onToggle={onToggleProtectedAreas} color="#dc2626" />
                {showProtectedAreas && (
                  <div style={{ display: 'flex', gap: '8px', marginTop: '4px', marginLeft: '22px', flexWrap: 'wrap' }}>
                    {[
                      { label: 'Military', color: '#dc2626' },
                      { label: 'Tribal', color: '#ea580c' },
                      { label: 'Monument/Park', color: '#16a34a' },
                      { label: 'Wilderness', color: '#15803d' },
                    ].map(c => (
                      <span key={c.label} style={{ fontSize: '9px', color: c.color, fontWeight: 600, display: 'flex', alignItems: 'center', gap: '3px' }}>
                        <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: c.color }} />
                        {c.label}
                      </span>
                    ))}
                  </div>
                )}
                <ToggleRow label="Critical Habitat" active={showCriticalHabitat} onToggle={onToggleCriticalHabitat} color="#dc2626" />
                {showCriticalHabitat && (
                  <div style={{ display: 'flex', gap: '8px', marginTop: '4px', marginLeft: '22px', flexWrap: 'wrap' }}>
                    {[
                      { label: 'Endangered', color: '#dc2626' },
                      { label: 'Threatened', color: '#f59e0b' },
                    ].map(c => (
                      <span key={c.label} style={{ fontSize: '9px', color: c.color, fontWeight: 600, display: 'flex', alignItems: 'center', gap: '3px' }}>
                        <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: c.color }} />
                        {c.label}
                      </span>
                    ))}
                  </div>
                )}
                <ToggleRow label="Wetlands (NWI)" active={showWetlands} onToggle={onToggleWetlands} color="#0ea5e9" />
                {showWetlands && (
                  <div style={{ display: 'flex', gap: '8px', marginTop: '4px', marginLeft: '22px', flexWrap: 'wrap' }}>
                    {[
                      { label: 'Pond', color: '#0ea5e9' },
                      { label: 'Lake', color: '#0284c7' },
                      { label: 'Riverine', color: '#6366f1' },
                      { label: 'Emergent', color: '#22c55e' },
                      { label: 'Forested', color: '#15803d' },
                    ].map(c => (
                      <span key={c.label} style={{ fontSize: '9px', color: c.color, fontWeight: 600, display: 'flex', alignItems: 'center', gap: '3px' }}>
                        <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: c.color }} />
                        {c.label}
                      </span>
                    ))}
                  </div>
                )}
                <ToggleRow label="Seismic Risk" active={showSeismicRisk} onToggle={onToggleSeismicRisk} color="#ef4444" />
                {showSeismicRisk && (
                  <div style={{ marginTop: '2px', marginLeft: '22px', marginBottom: '4px' }}>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                      {[
                        { label: 'Low', color: '#22c55e' },
                        { label: 'Moderate', color: '#eab308' },
                        { label: 'High', color: '#f97316' },
                        { label: 'Severe', color: '#ef4444' },
                      ].map(c => (
                        <span key={c.label} style={{ fontSize: '9px', color: c.color, fontWeight: 600, display: 'flex', alignItems: 'center', gap: '3px' }}>
                          <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: c.color }} />
                          {c.label}
                        </span>
                      ))}
                    </div>
                    <div style={{ fontSize: '9px', color: '#888', marginTop: '2px' }}>MMI VI+ probability in 100yr · USGS 2023</div>
                  </div>
                )}
                <ToggleRow label="Urban Areas" active={showUrbanAreas} onToggle={onToggleUrbanAreas} color="#6366f1" />
                <ToggleRow label="Wildfire Risk (WHP)" active={showWildfire} onToggle={onToggleWildfire} color="#ef4444" loading={wildfireLoading} />
                {showWildfire && (
                  <div style={{ marginTop: '2px', marginLeft: '22px', marginBottom: '4px' }}>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                      {[
                        { label: 'Very Low', color: '#16a34a' },
                        { label: 'Low', color: '#84cc16' },
                        { label: 'Moderate', color: '#eab308' },
                        { label: 'High', color: '#f97316' },
                        { label: 'Very High', color: '#dc2626' },
                      ].map(c => (
                        <span key={c.label} style={{ fontSize: '9px', color: c.color, fontWeight: 600, display: 'flex', alignItems: 'center', gap: '3px' }}>
                          <span style={{ width: '6px', height: '6px', borderRadius: '2px', background: c.color }} />
                          {c.label}
                        </span>
                      ))}
                    </div>
                    <div style={{ fontSize: '9px', color: '#888', marginTop: '2px' }}>USFS Wildfire Hazard Potential · county mean · hover for detail</div>
                  </div>
                )}
                <ToggleRow label="Mine Subsidence Zones (WV)" active={showMineSubsidence} onToggle={onToggleMineSubsidence} color="#7c2d12" />
                {showMineSubsidence && (
                  <div style={{ marginTop: '2px', marginLeft: '22px', marginBottom: '4px', fontSize: '9px', color: '#888' }}>
                    WVDEP underground mining limits · historic longwall/room-and-pillar mine footprints
                  </div>
                )}
                <ToggleRow label="AML Sites (WV)" active={showAmlSites} onToggle={onToggleAmlSites} color="#a16207" />
                {showAmlSites && (
                  <div style={{ marginTop: '2px', marginLeft: '22px', marginBottom: '4px' }}>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                      {[
                        { label: 'Emergency', color: '#dc2626' },
                        { label: 'Active', color: '#f97316' },
                        { label: 'Unfunded', color: '#eab308' },
                        { label: 'Completed', color: '#65a30d' },
                      ].map(c => (
                        <span key={c.label} style={{ fontSize: '9px', color: c.color, fontWeight: 600, display: 'flex', alignItems: 'center', gap: '3px' }}>
                          <span style={{ width: '6px', height: '6px', borderRadius: '2px', background: c.color }} />
                          {c.label}
                        </span>
                      ))}
                    </div>
                    <div style={{ fontSize: '9px', color: '#888', marginTop: '2px' }}>WVDEP Abandoned Mine Lands — subsidence, gob piles, slides, fires</div>
                  </div>
                )}
              </>
            )}

            {/* Land & Context */}
            <SectionHeader title="Land & Context" collapsed={collapsedSections.has('layersLand')} onToggle={() => toggleSection('layersLand')} anyActive={anyLandActive} onToggleAll={onToggleAllLand} />
            {!collapsedSections.has('layersLand') && (
              <>
                <ToggleRow label="Slope (East TX · 10m)" active={showSlopeDrape} onToggle={onToggleSlopeDrape} color="#16a34a" />
                {showSlopeDrape && (
                  <div style={{ marginTop: '2px', marginLeft: '22px', marginBottom: '4px' }}>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                      {[
                        { label: '< 2% prime', color: '#16a34a' },
                        { label: '2–5%', color: '#e8b878' },
                        { label: '5–10%', color: '#d97706' },
                        { label: '≥ 10% knockout', color: '#dc2626' },
                      ].map(c => (
                        <span key={c.label} style={{ fontSize: '9px', color: c.color, fontWeight: 600, display: 'flex', alignItems: 'center', gap: '3px' }}>
                          <span style={{ width: '6px', height: '6px', borderRadius: '2px', background: c.color }} />
                          {c.label}
                        </span>
                      ))}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px' }}>
                      <span style={{ fontSize: '9px', color: '#888' }}>Opacity</span>
                      <input
                        type="range" min={10} max={100} step={5}
                        value={Math.round(slopeDrapeOpacity * 100)}
                        onChange={e => onSlopeDrapeOpacityChange(Number(e.target.value) / 100)}
                        style={{ flex: 1, maxWidth: '90px', accentColor: '#16a34a', cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '9px', color: '#888', minWidth: '26px' }}>{Math.round(slopeDrapeOpacity * 100)}%</span>
                    </div>
                    <div style={{ fontSize: '9px', color: '#888', marginTop: '2px' }}>USGS 3DEP <strong>10 m screening grid</strong>, same source as parcel avg_slope_pct · classed at the scoring breaks</div>
                    <div style={{ fontSize: '9px', color: designTokens.colors.warning, marginTop: '2px' }}>10 m smooths ETX terrain and reads flatter than it is — 1 m lidar on A1 gave 40% under 5%, not 78%. Screen with this; site on 1 m. Blockiness at high zoom is the true 10 m cell size, not a rendering artifact.</div>
                  </div>
                )}
                <ToggleRow label="Slope · 1 m lidar (13 sites)" active={showSlope1m} onToggle={onToggleSlope1m} color="#16a34a" />
                {showSlope1m && (
                  <div style={{ marginTop: '2px', marginLeft: '22px', marginBottom: '4px' }}>
                    <div style={{ fontSize: '9px', color: '#888' }}>
                      Same four classes at USGS 3DEP <strong>1 m</strong>, around the capped Cycle-3 site blocks and A1 alternatives. z13–z17. Draws above the 10 m layer where they overlap.
                    </div>
                    <div style={{ fontSize: '9px', color: designTokens.colors.success, marginTop: '2px' }}>
                      Use this 1 m view to inspect local grade changes that the 10 m regional screen smooths over.
                    </div>
                  </div>
                )}
                <ToggleRow label="Data Centers" active={showDataCenters} onToggle={onToggleDataCenters} color="#0d9488" loading={dcLoading} />
                <ToggleRow label="Federal Lands" active={showFederalLands} onToggle={onToggleFederalLands} color="#b45309" />
                {showFederalLands && (
                  <div style={{ display: 'flex', gap: '8px', marginTop: '4px', marginLeft: '22px', flexWrap: 'wrap' }}>
                    {[
                      { label: 'BLM', color: '#b45309' },
                      { label: 'USFS', color: '#166534' },
                      { label: 'NPS', color: '#92400e' },
                      { label: 'State', color: '#7c3aed' },
                      { label: 'Private', color: '#059669' },
                      { label: 'Other Fed', color: '#dc2626' },
                    ].map(c => (
                      <span key={c.label} style={{ fontSize: '9px', color: c.color, fontWeight: 600, display: 'flex', alignItems: 'center', gap: '3px' }}>
                        <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: c.color }} />
                        {c.label}
                      </span>
                    ))}
                  </div>
                )}
                <ToggleRow label="Crown Pine Tracts (LRM)" active={showCrownPineTracts} onToggle={onToggleCrownPineTracts} color="#9a3412" />
                {showCrownPineTracts && (
                  <div style={{ marginTop: '4px', marginLeft: '22px' }}>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {[
                        { label: 'CPT1 · Timber 1 LP', color: '#9a3412' },
                        { label: 'CPT5 · Timber 5 LP', color: '#d97706' },
                      ].map(c => (
                        <span key={c.label} style={{ fontSize: '9px', color: c.color, fontWeight: 600, display: 'flex', alignItems: 'center', gap: '3px' }}>
                          <span style={{ width: '6px', height: '6px', borderRadius: '2px', background: c.color }} />
                          {c.label}
                        </span>
                      ))}
                    </div>
                    <div style={{ fontSize: '9px', color: '#888', marginTop: '2px' }}>Crown Pine's own tract inventory (LRM export, Aug 2026) — 1,853 tracts, ~903K ac, East TX + West LA</div>
                  </div>
                )}
                <ToggleRow label="Assembly Groups" active={showAssemblyGroups} onToggle={onToggleAssemblyGroups} color="#2563eb" />
                <ToggleRow label="State Trust Lands" active={showStateTrustLands} onToggle={onToggleStateTrustLands} color="#16a34a" loading={trustLandLoading} />
                <ToggleRow label="Land for Sale (BTM)" active={showLandListings} onToggle={onToggleLandListings} color="#ec4899" loading={listingsLoading} />
                <div style={{
                  width: '100%', padding: '5px 8px', fontSize: '12px',
                  display: 'flex', alignItems: 'center', gap: '8px',
                  color: theme.textTertiary, cursor: 'default', opacity: 0.55,
                }}>
                  <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: theme.textTertiary, flexShrink: 0 }} />
                  <span>Land for Sale (Grid)</span>
                  <span style={{ fontSize: '9px', fontStyle: 'italic' }}>temp disabled</span>
                </div>
              </>
            )}

            {/* Saved Locations — user-bookmarked pins */}
            <SavedLocationsSection
              locations={savedLocations}
              loading={savedLocationsLoading}
              visible={showSavedLocations}
              onToggleVisible={onToggleSavedLocations}
              onClickLocation={onClickSavedLocation}
              onRename={onRenameSavedLocation}
              onDelete={onDeleteSavedLocation}
            />
          </>
        )}
        <div data-testid="one-off-sites">
          <GroupHeader title="One-offs" collapsed={collapsedSections.has('oneOffs')} onToggle={() => toggleSection('oneOffs')} />
          {!collapsedSections.has('oneOffs') && <div style={{ paddingLeft: '8px' }}>
            <div style={{ fontSize: '10px', color: theme.textSecondary, margin: '6px 0' }}>Individual sites and research requests</div>
            {memphisLayers && onMemphisLayersChange && onMemphisZoom && onToggleAllMemphis && <>
              <GroupHeader title="Memphis, Texas" collapsed={collapsedSections.has('memphis')} onToggle={() => toggleSection('memphis')} active={Object.values(memphisLayers).some(Boolean)} onToggleActive={onToggleAllMemphis} />
              {!collapsedSections.has('memphis') && <MemphisResearchSection layers={memphisLayers} onChange={onMemphisLayersChange} onZoom={onMemphisZoom} />}
            </>}

            {onToggleAllGeorgia && (<>
            <GroupHeader title="Georgia · Dalton" collapsed={collapsedSections.has('georgia')} onToggle={() => toggleSection('georgia')} active={!!anyGeorgiaActive} onToggleActive={onToggleAllGeorgia} />
            {!collapsedSections.has('georgia') && (<>
              <ToggleRow label="Dalton site outline" active={!!showGeorgiaSites} onToggle={onToggleGeorgiaSites ?? (() => {})} color="#1e2a3a" />
              <div style={{ marginTop: '2px', marginLeft: '22px', marginBottom: '4px', fontSize: '9px', color: '#888' }}>
                Whitfield County parcel 13-030-01-000 · 328.12 ac recorded · county GIS outline, not a survey
              </div>
              <ToggleRow label="OSM Pipeline Candidates" active={!!showOsmPipelineCandidates} onToggle={onToggleOsmPipelineCandidates ?? (() => {})} color={designTokens.colors.pipelineGasTransmission} loading={osmPipelineCandidatesLoading} />
              <div style={{ marginTop: '2px', marginLeft: '22px', marginBottom: '4px', fontSize: '9px', color: '#888' }}>
                Crowd-mapped corridors · Georgia · not scored · © OpenStreetMap contributors (ODbL)
              </div>
              <ToggleRow label="Line evidence pins" active={!!showGeorgiaEvidence} onToggle={onToggleGeorgiaEvidence ?? (() => {})} color={designTokens.colors.brand} />
              <div style={{ marginTop: '2px', marginLeft: '22px', marginBottom: '4px', fontSize: '9px', color: '#888' }}>
                Where a FERC, PHMSA or operator document pins a diameter, meter, valve or interconnect to SNG, ETNG or Transco · click a pin for the source and distance
              </div>
              {onZoomToGeorgiaSite && (
                <button
                  onClick={onZoomToGeorgiaSite}
                  style={{ marginLeft: '22px', marginTop: '2px', marginBottom: '4px', fontSize: '11px', background: 'none', border: 'none', color: designTokens.colors.brand, cursor: 'pointer', padding: 0, textDecoration: 'underline', fontFamily: designTokens.typography.fontFamily.display }}
                >
                  Zoom to Dalton site
                </button>
              )}
            </>)}
            </>)}

            <OneOffBriefingLinks />
          </div>}
        </div>
      </div>

      {/* Sticky footer: active count + clear all */}
      {(activeLayerCount > 0 || showParcels || anyPipelineActive) && (
        <div style={{
          padding: '8px 12px',
          borderTop: `1px solid ${theme.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <span style={{
            fontSize: '10px', color: theme.textTertiary,
            fontFamily: designTokens.typography.fontFamily.mono,
          }}>
            {activeLayerCount + (showParcels ? 1 : 0) + (anyPipelineActive ? 1 : 0)} active
          </span>
          <button
            onClick={() => {
              onResetAllLayers();
              if (showParcels) onToggleParcels();
              if (anyPipelineActive) onToggleAllPipelines();
            }}
            style={{
              fontSize: '10px', padding: '3px 10px', borderRadius: '4px',
              border: `1px solid ${theme.border}`, background: theme.background,
              color: theme.textSecondary, cursor: 'pointer',
              fontFamily: designTokens.typography.fontFamily.display, fontWeight: 600,
            }}
          >
            Clear All
          </button>
        </div>
      )}
    </div>
  );
};

function OneOffBriefingLinks() {
  const { theme } = useTheme();
  const [zones, setZones] = useState<ZoneListEntry[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    fetchZoneList(controller.signal)
      .then(setZones)
      .catch(() => { if (!controller.signal.aborted) setFailed(true); })
      .finally(() => { if (!controller.signal.aborted) setLoaded(true); });
    return () => controller.abort();
  }, []);
  return <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '12px' }}>
    {ONE_OFF_SITES.map(site => {
      const zone = zones.find(z => z.slug === site.slug);
      const style = { padding: '7px 0', fontSize: '11px', borderTop: `1px solid ${theme.borderLight}`, textDecoration: 'none' };
      return zone ? <a key={site.slug} href={`#/zones/${zone.slug}`} style={{ ...style, color: theme.link }}>{site.label}{zone.archived ? ' (archived)' : ''} →</a>
        : <div key={site.slug} style={{ ...style, color: theme.textSecondary }}>{site.label}<span style={{ marginLeft: '6px', fontSize: '10px', color: theme.textTertiary }}>{!loaded ? 'Loading…' : failed ? 'Unavailable' : site.slug === 'cooper-draw' ? 'Upcoming' : 'Unavailable'}</span></div>;
    })}
  </div>;
}

function ZoneListLinks() {
  const [zones, setZones] = useState<ZoneListEntry[]>([]);
  useEffect(() => {
    const controller = new AbortController();
    fetchZoneList(controller.signal)
      .then(rows => setZones(rows.filter(z => !isOneOffZone(z.slug))))
      .catch(() => setZones([]));
    return () => controller.abort();
  }, []);
  if (zones.length === 0) return <div style={{ fontSize: 11, color: '#9ca3af' }}>None.</div>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {[...zones].sort((a, b) => Number(a.archived) - Number(b.archived)).map(z => (
        <a
          key={z.slug}
          href={`#/zones/${z.slug}`}
          style={{ fontSize: 11, color: z.archived ? '#9ca3af' : '#0f766e', textDecoration: 'none' }}
        >
          {z.operator_name} — {z.region_label} {z.archived ? '(archived) ' : ''}→
        </a>
      ))}
    </div>
  );
}
